import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  Clock,
  Users,
  Shield,
  BarChart3,
  CheckCircle,
  Globe,
  Zap,
  FileText,
  Award,
} from "lucide-react";
import { useEffect } from "react";

const GovernmentSectorLanding = () => {
  useEffect(() => {
    document.title =
      "NationCard™ for Government & Public Sector - Real-Time Citizen Sentiment Platform";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-[97px] pb-16 md:pt-[105px] md:pb-24 overflow-hidden bg-gradient-to-br from-[#002566] via-nation-blue-dark/85 to-[#657fad]">
        {/* Background overlay with hero image */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/hero-13-1920x480.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#002566]/90 via-nation-blue-dark/85 to-[#657fad]/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <Shield size={16} className="mr-2" />
                    <span>Trusted by Government Agencies</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      NationCard™ for{" "}
                      <span className="text-nation-teal">
                        Government & Public Sector
                      </span>
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
                      Make policy decisions with confidence—leverage live,
                      verified citizen sentiment.
                    </p>
                    <p className="text-lg md:text-xl opacity-80 leading-relaxed text-nation-teal">
                      Transparent governance through real-time public insights
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-lg px-8 py-4 bg-nation-teal hover:bg-nation-teal-dark text-white h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() =>
                        (window.location.href = "/solutions/government")
                      }
                    >
                      Request a Demo
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      Download Case Studies
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                    <div className="flex items-center gap-2">
                      <Globe size={16} />
                      <span>25+ Countries</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 size={16} />
                      <span>Real-time Analytics</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      <span>Live Data Stream</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    {/* Mock Dashboard Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-nation-teal rounded-lg flex items-center justify-center">
                          <BarChart3 size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">
                            Citizen Sentiment
                          </div>
                          <div className="text-white/60 text-sm">
                            Government Dashboard
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                    </div>

                    {/* Mock Charts */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Policy Support
                        </div>
                        <div className="text-green-400 text-2xl font-bold">
                          78%
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div className="bg-green-400 h-2 rounded-full w-4/5"></div>
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Active Citizens
                        </div>
                        <div className="text-nation-teal text-2xl font-bold">
                          2.4M
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                          +12% this week
                        </div>
                      </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="space-y-2">
                      <div className="text-white/80 text-xs">
                        Recent Activity
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>Healthcare policy +15%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <span>Economic confidence -3%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          <span>Environmental alerts +8%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating verification indicator */}
                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-white text-xs">Verified Data</div>
                  <div className="text-nation-teal font-bold">99.7%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Challenges Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Your Challenges
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Traditional engagement methods are failing modern governance
                needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Clock className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Slow Traditional Surveys
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Costly surveys delay insights when quick decisions are
                    critical
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <BarChart3 className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    No Real-Time Feedback
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Lack of immediate public response to policy proposals
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Shield className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Data Integrity Issues
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Difficulty ensuring compliance and data security standards
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Users className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Public Backlash Risk
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Opaque engagement leads to unexpected public opposition
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                How We Help You
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Transform how you engage with citizens and make informed
                decisions
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <BarChart3 className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Real-Time Sentiment Dashboards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Monitor public opinion live across regions and channels with
                    intuitive, customizable dashboards that update in real-time.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                    <Users className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Verified Citizen Panels
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Ensure data integrity with secure, identity-vetted
                    participants providing authentic feedback on government
                    initiatives.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                    <Shield className="h-8 w-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Compliance & Data Security
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Built-in GDPR and local-law compliance for effortless
                    governance with enterprise-grade security and audit trails.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Key Benefits & Capabilities
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Globe className="h-6 w-6 text-nation-blue flex-shrink-0" />
                <span className="font-medium">25+ countries tracked</span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Zap className="h-6 w-6 text-nation-purple flex-shrink-0" />
                <span className="font-medium">Custom alert rules</span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <FileText className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">Seamless API & export</span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Award className="h-6 w-6 text-orange-600 flex-shrink-0" />
                <span className="font-medium">Dedicated account support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader>
                <div className="text-center">
                  <Badge className="mb-4 bg-blue-100 text-blue-700">
                    Case Study
                  </Badge>
                  <CardTitle className="text-2xl md:text-3xl text-nation-blue">
                    City of Springfield: Faster Policy Feedback
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      50%
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Reduction in survey lead times
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      10+
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Zoning proposals with real-time feedback
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      85%
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Citizen satisfaction increase
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                  >
                    Read Full Case Study
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-nation-blue mb-8">
                Trusted by Government Agencies Worldwide
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60 mb-12">
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Ministry of Innovation
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Department of Planning
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  City Council Authority
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Public Affairs Bureau
                </div>
              </div>

              <Card className="max-w-2xl mx-auto hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8">
                  <blockquote className="text-lg italic text-muted-foreground mb-6">
                    "NationCard™ transformed how we engage with citizens.
                    Real-time feedback has made our policy development 3x faster
                    and significantly more responsive to public needs."
                  </blockquote>
                  <div className="text-center">
                    <div className="font-semibold">Sarah Mitchell</div>
                    <div className="text-sm text-muted-foreground">
                      Director of Public Engagement
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Metropolitan Planning Department
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-nation-blue via-nation-purple to-nation-teal">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              See NationCard™ in Action
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ready to transform your citizen engagement? Book a personalized
              demo today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-white text-nation-blue hover:bg-white/90 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default GovernmentSectorLanding;
